import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { App as AntApp, Button, Checkbox, Input, Modal, Select, Space, Switch, Table, Tag, Tree, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGroupMembers,
  useModelGroups,
  useSaveModelGroup,
  useUpdateGroupMember,
} from '../../api/hooks';
import type { ModelGroupMember, ParamMap, SubSourceKind } from '../../api/types';
import { extractErrorMessage } from '../../api/client';
import { canonicalName } from '@gw/shared';
import { resolveModelParams, type ParamField, type ParamType } from './modelParams';

const NONE = '__none__';
const TYPE_OPTIONS: { value: ParamType; label: string }[] = [
  { value: 'textarea', label: 'Văn bản dài' },
  { value: 'text', label: 'Văn bản' },
  { value: 'number', label: 'Số' },
  { value: 'select', label: 'Chọn (select)' },
  { value: 'bool', label: 'Bật/tắt' },
  { value: 'json', label: 'JSON' },
  { value: 'color', label: 'Màu' },
  { value: 'image', label: 'Ảnh' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Âm thanh' },
  { value: 'media', label: 'Ảnh/Video' },
  { value: 'image[]', label: 'Nhiều ảnh' },
  { value: 'video[]', label: 'Nhiều video' },
];

/** Field thật của 1 member (model → paramsSchema/fallback; nguồn con → tối thiểu theo thao tác). */
function memberFieldsOf(member: ModelGroupMember): ParamField[] {
  if (member.model) return resolveModelParams(member.model);
  const kind = (member.modelKind as SubSourceKind) || 'chat';
  // Ảnh dùng CHUNG (tạo & sửa): prompt + ảnh tham chiếu tuỳ chọn (có = sửa, trống = tạo).
  if (kind === 'image')
    return [
      { name: 'prompt', label: 'Prompt', type: 'textarea' },
      { name: 'size', label: 'Kích thước / tỷ lệ', type: 'select', options: ['auto', '1:1', '3:4', '9:16', '4:3', '16:9'] },
      { name: 'images', label: 'Ảnh tham chiếu (có = sửa, trống = tạo)', type: 'image[]' },
    ];
  return [
    { name: 'prompt', label: 'Prompt', type: 'textarea', required: true },
    { name: 'images', label: 'Ảnh (multi-modal)', type: 'image[]' },
  ];
}

function memberLabel(m: ModelGroupMember): string {
  if (m.model) return `${m.model.provider?.name ?? '?'} · ${m.model.name}`;
  return `${m.subSource?.provider?.name ?? '?'} · ${m.subSource?.name} (${m.modelKind})`;
}

function parseOptions(tokens: string[]): (string | number)[] {
  return tokens.map((t) => (t !== '' && !Number.isNaN(Number(t)) ? Number(t) : t));
}

interface CanonRow {
  key: string;
  name: string;
  label: string;
  type: ParamType;
  options: string[]; // giá trị chung (canonical) — chuỗi
  required: boolean;
  enabled: boolean; // false = ẩn thông số (không expose ra catalog)
  default: string;
  refs: Record<string, string | null>; // memberId -> field name (null = không hỗ trợ)
}

/** Popup chọn GIÁ TRỊ dạng cây nhiều cấp: model con → tham số → giá trị, có "chọn tất cả" mỗi cấp. */
function ValuePickerModal({
  open,
  onClose,
  onApply,
  initial,
  members,
  memberFields,
}: {
  open: boolean;
  onClose: () => void;
  onApply: (values: string[]) => void;
  initial: string[];
  members: ModelGroupMember[];
  memberFields: Map<string, ParamField[]>;
}) {
  const treeData = useMemo(
    () =>
      members.map((mem) => ({
        title: memberLabel(mem),
        key: `m:${mem.id}`,
        selectable: false,
        children: (memberFields.get(mem.id) || [])
          .filter((f) => f.options?.length)
          .map((f) => ({
            title: `${f.name} (${f.type})`,
            key: `m:${mem.id}|f:${f.name}`,
            selectable: false,
            children: (f.options || []).map((o) => ({
              title: String(o),
              key: `m:${mem.id}|f:${f.name}|v:${String(o)}`,
              selectable: false,
              isLeaf: true,
            })),
          })),
      })),
    [members, memberFields],
  );

  const allLeafKeys = useMemo(() => {
    const out: string[] = [];
    for (const m of treeData) for (const f of m.children) for (const v of f.children) out.push(v.key);
    return out;
  }, [treeData]);

  const valOf = (k: string) => k.split('|v:')[1];
  const [checked, setChecked] = useState<string[]>([]);
  useEffect(() => {
    if (!open) return;
    const set = new Set(initial.map((s) => s.toLowerCase()));
    setChecked(allLeafKeys.filter((k) => set.has(valOf(k).toLowerCase())));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const apply = () => {
    const vals = Array.from(new Set(checked.filter((k) => k.includes('|v:')).map(valOf)));
    onApply(vals);
    onClose();
  };

  return (
    <Modal
      title="Chọn giá trị (gom theo model → tham số → giá trị)"
      open={open}
      onCancel={onClose}
      onOk={apply}
      okText="Áp dụng"
      cancelText="Hủy"
      width={640}
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
    >
      <Space style={{ marginBottom: 8 }}>
        <Button size="small" onClick={() => setChecked(allLeafKeys)}>
          Chọn tất cả
        </Button>
        <Button size="small" onClick={() => setChecked([])}>
          Bỏ chọn tất cả
        </Button>
        <Typography.Text type="secondary">Tick ở cấp model/tham số = chọn tất cả con của nó.</Typography.Text>
      </Space>
      <Tree
        checkable
        selectable={false}
        defaultExpandAll={false}
        treeData={treeData}
        checkedKeys={checked}
        onCheck={(keys) => setChecked((Array.isArray(keys) ? keys : keys.checked) as string[])}
      />
    </Modal>
  );
}

/**
 * Trình tạo "thông số trung gian" (canonical) cho nhóm trong MỘT bảng:
 *  - Tên thông số (key gửi API)
 *  - Giá trị (chọn từ union option của các model)
 *  - Tham chiếu model con: mỗi model chọn field tương ứng (đã chọn ở dòng khác sẽ bị loại trừ)
 * Lưu = ghi group.paramsSchema (canonical) + paramMap cho từng member (kèm map giá trị tự suy).
 */
export function GroupCanonicalParams({ groupId }: { groupId: string }) {
  const { message } = AntApp.useApp();
  const membersQ = useGroupMembers(groupId);
  const groupsQ = useModelGroups();
  const saveGroup = useSaveModelGroup();
  const updateMember = useUpdateGroupMember(groupId);

  const members = useMemo(() => membersQ.data ?? [], [membersQ.data]);
  const group = groupsQ.data?.find((g) => g.id === groupId);

  // memberId -> field list + map theo tên
  const memberFields = useMemo(() => {
    const m = new Map<string, ParamField[]>();
    for (const mem of members) m.set(mem.id, memberFieldsOf(mem));
    return m;
  }, [members]);

  const uid = useRef(1);
  const [rows, setRows] = useState<CanonRow[]>([]);

  // Seed từ group.paramsSchema + member.paramMap (đảo ngược) khi đổi nhóm/dữ liệu.
  useEffect(() => {
    const canon = (group?.paramsSchema as ParamField[]) || [];
    const seeded: CanonRow[] = canon.map((c) => {
      const refs: Record<string, string | null> = {};
      for (const mem of members) {
        const pm = mem.paramMap as ParamMap | undefined;
        const to = pm?.fields?.[c.name]?.to;
        refs[mem.id] = to !== undefined ? to : (memberFields.get(mem.id)?.some((f) => f.name === c.name) ? c.name : null);
      }
      return {
        key: `k${uid.current++}`,
        name: c.name,
        label: c.label || c.name,
        type: c.type,
        options: (c.options || []).map((o) => String(o)),
        required: !!c.required,
        enabled: (c as any).enabled !== false,
        default: c.default === undefined ? '' : String(c.default),
        refs,
      };
    });
    setRows(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, membersQ.data]);

  const patch = (i: number, p: Partial<CanonRow>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const setRef = (i: number, memberId: string, val: string | null) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, refs: { ...r.refs, [memberId]: val } } : r)));

  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { key: `k${uid.current++}`, name: '', label: '', type: 'select', options: [], required: false, enabled: true, default: '', refs: {} },
    ]);
  const removeAt = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const [pickerRow, setPickerRow] = useState<number | null>(null);

  // Tự tạo: gom field của các model theo TÊN CANONICAL (không theo tên thô), nên
  // `image_urls` (TramSangTao) + `reference_images` (Plenxai) + `startImageUrls` (Kapend)
  // về CÙNG 1 thông số `reference_images` — phần mềm thứ 3 chỉ thấy 1 tên duy nhất.
  // Kiểu chọn dạng MẢNG nếu có model dùng mảng (Gateway tự cắt về 1 URL cho model chỉ nhận 1).
  const autoBuild = () => {
    const byName = new Map<string, CanonRow>();
    for (const mem of members) {
      for (const f of memberFields.get(mem.id) || []) {
        const canon = canonicalName(f.name);
        let row = byName.get(canon);
        if (!row) {
          row = {
            key: `k${uid.current++}`,
            name: canon,
            label: f.label || canon,
            type: f.type,
            options: (f.options || []).map((o) => String(o)),
            required: !!f.required,
            enabled: true,
            default: f.default === undefined ? '' : String(f.default),
            refs: {},
          };
          byName.set(canon, row);
        } else {
          if (f.options) row.options = Array.from(new Set([...row.options, ...f.options.map((o) => String(o))]));
          // Ưu tiên kiểu MẢNG khi các model lệch số lượng (image ↔ image[], video ↔ video[]).
          if (f.type === 'image[]' && row.type === 'image') row.type = 'image[]';
          if (f.type === 'video[]' && row.type === 'video') row.type = 'video[]';
        }
        // Model này đã có field khác trỏ vào canonical này (vd cả `img_url` lẫn `image_urls`
        // đều là ảnh) -> giữ ref đầu tiên, field còn lại để admin tự trỏ nếu cần.
        if (!row.refs[mem.id]) row.refs[mem.id] = f.name;
      }
    }
    // các member không có field cùng tên -> null
    for (const row of byName.values()) for (const mem of members) if (!(mem.id in row.refs)) row.refs[mem.id] = null;
    setRows(Array.from(byName.values()));
  };

  // Tìm member field theo tên (để suy valueMap + type).
  const findField = (memberId: string, name: string) =>
    memberFields.get(memberId)?.find((f) => f.name === name);

  const onSave = async () => {
    const clean = rows.filter((r) => r.name.trim());
    // 1) group.paramsSchema (canonical) — GIỮ cả thông số đã TẮT (enabled:false) để nhớ trạng thái.
    const paramsSchema: ParamField[] = clean.map((r) => {
      const f: ParamField = { name: r.name.trim(), label: r.label || r.name, type: r.type };
      if (r.type === 'select' && r.options.length) f.options = parseOptions(r.options);
      if (r.required) f.required = true;
      if (!r.enabled) f.enabled = false;
      if (r.default !== '') f.default = r.default;
      return f;
    });
    // 2) paramMap cho từng member — CHỈ ánh xạ thông số đang BẬT (kèm valueMap tự suy khớp hoa/thường).
    const memberMaps = new Map<string, ParamMap>();
    for (const mem of members) memberMaps.set(mem.id, { fields: {}, defaults: {} });
    for (const r of clean) {
      if (!r.enabled) continue;
      for (const mem of members) {
        const to = r.refs[mem.id] ?? null;
        const entry: { to: string | null; valueMap?: Record<string, unknown> } = { to };
        if (to && r.type === 'select' && r.options.length) {
          const mf = findField(mem.id, to);
          if (mf?.options?.length) {
            const vm: Record<string, string> = {};
            for (const opt of r.options) {
              const match = mf.options.find((mo) => String(mo).toLowerCase() === opt.toLowerCase());
              if (match !== undefined && String(match) !== opt) vm[opt] = String(match);
            }
            if (Object.keys(vm).length) entry.valueMap = vm;
          }
        }
        memberMaps.get(mem.id)!.fields![r.name.trim()] = entry;
      }
    }
    try {
      await saveGroup.mutateAsync({ id: groupId, data: { paramsSchema: paramsSchema.length ? paramsSchema : null } as any });
      await Promise.all(members.map((mem) => updateMember.mutateAsync({ memberId: mem.id, body: { paramMap: memberMaps.get(mem.id)! } })));
      message.success('Đã lưu tham số nhóm & ánh xạ cho từng model');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  // Field đã được dùng bởi dòng khác (để loại trừ dần) — theo từng member.
  const usedByOthers = (rowIdx: number, memberId: string) => {
    const used = new Set<string>();
    rows.forEach((r, idx) => {
      if (idx === rowIdx) return;
      const v = r.refs[memberId];
      if (v) used.add(v);
    });
    return used;
  };

  const columns = [
    {
      title: 'Tên thông số (key gửi API)',
      key: 'name',
      width: 180,
      render: (_: unknown, _r: CanonRow, i: number) => (
        <Space direction="vertical" size={2} style={{ width: 170 }}>
          <Input
            size="small"
            placeholder="vd: aspect_ratio_new"
            value={rows[i].name}
            onChange={(e) => patch(i, { name: e.target.value })}
          />
          <Select
            size="small"
            style={{ width: 170 }}
            value={rows[i].type}
            options={TYPE_OPTIONS}
            onChange={(v) => patch(i, { type: v })}
          />
        </Space>
      ),
    },
    {
      title: 'Giá trị (chọn từ các model)',
      key: 'options',
      width: 280,
      render: (_: unknown, _r: CanonRow, i: number) =>
        rows[i].type === 'select' ? (
          <Space direction="vertical" size={4} style={{ width: 260 }}>
            <Button size="small" onClick={() => setPickerRow(i)}>
              Chọn giá trị ({rows[i].options.length})
            </Button>
            <div>
              {rows[i].options.slice(0, 8).map((o) => (
                <Tag key={o} style={{ marginBottom: 2 }}>
                  {o}
                </Tag>
              ))}
              {rows[i].options.length > 8 ? <Tag>+{rows[i].options.length - 8}</Tag> : null}
            </div>
          </Space>
        ) : (
          <span style={{ color: '#ccc' }}>— (không phải select)</span>
        ),
    },
    {
      title: 'Tham chiếu model con (field tương ứng)',
      key: 'refs',
      render: (_: unknown, _r: CanonRow, i: number) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {members.map((mem) => {
            const fields = memberFields.get(mem.id) || [];
            const used = usedByOthers(i, mem.id);
            const cur = rows[i].refs[mem.id] ?? NONE;
            return (
              <Space key={mem.id} size={6} style={{ width: '100%' }}>
                <span style={{ width: 230, display: 'inline-block', fontSize: 12 }}>{memberLabel(mem)}</span>
                <Select
                  size="small"
                  style={{ width: 200 }}
                  value={cur}
                  onChange={(v) => setRef(i, mem.id, v === NONE ? null : v)}
                  options={[
                    { value: NONE, label: '(không hỗ trợ)' },
                    ...fields
                      .filter((f) => !used.has(f.name)) // loại trừ dần field đã chọn ở dòng khác
                      .map((f) => ({ value: f.name, label: `${f.name} (${f.type})` })),
                  ]}
                />
              </Space>
            );
          })}
        </Space>
      ),
    },
    {
      title: 'Bắt buộc',
      key: 'required',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, _r: CanonRow, i: number) => (
        <Checkbox checked={rows[i].required} onChange={(e) => patch(i, { required: e.target.checked })} />
      ),
    },
    {
      title: 'Bật',
      key: 'enabled',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, _r: CanonRow, i: number) => (
        <Switch
          size="small"
          checked={rows[i].enabled}
          onChange={(v) => patch(i, { enabled: v })}
        />
      ),
    },
    {
      title: '',
      key: 'ops',
      width: 44,
      render: (_: unknown, _r: CanonRow, i: number) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeAt(i)} />
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }} wrap>
        <Typography.Text type="secondary">
          Tạo <b>thông số trung gian</b> cho nhóm: đặt tên, chọn giá trị chung, và trỏ tới field
          tương ứng của từng model. Field đã trỏ sẽ bị loại trừ ở dòng sau. Đây chính là tham số
          phần mềm thứ 3 thấy &amp; gửi qua <code>/api/v1/catalog</code>.
          <br />
          Nên dùng <b>tên canonical</b> (<code>reference_images</code> = chỉ ẢNH,{' '}
          <code>reference_videos</code>/<code>input_video</code> = chỉ VIDEO). Gateway tự bỏ link sai
          loại, tự cắt/bọc mảng theo model con và tự quy giá trị về option của nguồn (vd{' '}
          <code>duration</code> "8" → "8s" hoặc 8).
        </Typography.Text>
        <Space>
          <Button onClick={autoBuild}>Tự tạo từ các model</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saveGroup.isPending || updateMember.isPending} onClick={onSave}>
            Lưu tham số nhóm
          </Button>
        </Space>
      </Space>
      <Table<CanonRow>
        rowKey="key"
        size="small"
        dataSource={rows}
        columns={columns}
        pagination={false}
        loading={membersQ.isLoading}
        onRow={(r) => ({ style: r.enabled ? undefined : { opacity: 0.45 } })}
        locale={{ emptyText: 'Chưa có thông số — bấm "Thêm thông số" hoặc "Tự tạo từ các model".' }}
      />
      <Button icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 8 }}>
        Thêm thông số
      </Button>

      <ValuePickerModal
        open={pickerRow !== null}
        onClose={() => setPickerRow(null)}
        initial={pickerRow !== null ? rows[pickerRow].options : []}
        members={members}
        memberFields={memberFields}
        onApply={(vals) => {
          if (pickerRow !== null) patch(pickerRow, { options: vals });
        }}
      />
    </div>
  );
}
